import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError, fmtMoney } from "../../lib/api";
import { toast } from "sonner";
import { useLeague } from "./LeagueLayout";
import { Search, TrendingUp, TrendingDown, Filter, Lock, ArrowRightLeft, X, BookOpen, Link2 } from "lucide-react";
import { PlayerNameLink } from "../../lib/playerLink";

const ROLE_STYLES = {
  P: "bg-amber-500/20 text-amber-300 border-amber-500/30",
  D: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  C: "bg-blue-500/20 text-blue-300 border-blue-500/30",
  A: "bg-rose-500/20 text-rose-300 border-rose-500/30",
};

// Colori Mantra per famiglia + variante. Ogni ruolo Mantra ha una tinta specifica
// che si nota subito dal pallino grande in tabella e nella scheda giocatore.
const MANTRA_STYLES = {
  // Portiere
  POR: { bg: "bg-amber-400", ring: "ring-amber-300",   text: "text-slate-900", label: "Portiere",           family: "P" },
  // Difensori (varianti verde/teal)
  DC:  { bg: "bg-emerald-500", ring: "ring-emerald-300", text: "text-white",    label: "Difensore Centrale", family: "D" },
  DD:  { bg: "bg-emerald-400", ring: "ring-emerald-200", text: "text-slate-900",label: "Difensore Destro",   family: "D" },
  DS:  { bg: "bg-teal-500",   ring: "ring-teal-300",   text: "text-white",    label: "Difensore Sinistro", family: "D" },
  B:   { bg: "bg-teal-600",   ring: "ring-teal-400",   text: "text-white",    label: "Braccetto",          family: "D" },
  E:   { bg: "bg-cyan-500",   ring: "ring-cyan-300",   text: "text-slate-900",label: "Esterno basso",      family: "D" },
  // Centrocampisti (blu/indaco)
  M:   { bg: "bg-blue-500",   ring: "ring-blue-300",   text: "text-white",    label: "Mediano",            family: "C" },
  C:   { bg: "bg-indigo-500", ring: "ring-indigo-300", text: "text-white",    label: "Centrocampista",     family: "C" },
  // Offensivi (viola/fucsia)
  T:   { bg: "bg-violet-500", ring: "ring-violet-300", text: "text-white",    label: "Trequartista",       family: "C" },
  W:   { bg: "bg-fuchsia-500",ring: "ring-fuchsia-300",text: "text-white",    label: "Ala",                family: "A" },
  // Attaccanti (arancio/rosso)
  A:   { bg: "bg-orange-500", ring: "ring-orange-300", text: "text-white",    label: "Attaccante",         family: "A" },
  PC:  { bg: "bg-rose-500",   ring: "ring-rose-300",   text: "text-white",    label: "Punta Centrale",     family: "A" },
};

function mantraStyle(role) {
  const key = String(role || "").toUpperCase();
  return MANTRA_STYLES[key] || { bg: "bg-slate-600", ring: "ring-slate-500", text: "text-white", label: key, family: "" };
}

/**
 * splitMantraTokens — normalizza un array di token Mantra splittando
 * eventuali stringhe concatenate legacy ("CT" → ["C","T"], "MC" → ["M","C"]).
 * Backend è stato fixato (iter 58), ma questa difesa evita pallini grigi con
 * dati non ancora migrati (es. produzione pre-redeploy o cache).
 * Riconosce i multi-char VALIDI: POR, DC, DD, DS, PC (tutto il resto è
 * splittato char-by-char se ogni char è un ruolo valido).
 */
const _VALID_MANTRA_MULTI = new Set(["POR", "DC", "DD", "DS", "PC"]);
const _VALID_MANTRA_SINGLE = new Set(["P", "D", "B", "E", "M", "C", "T", "W", "A"]);

// Ordine tattico Mantra (dal portiere all'attacco). Usato per ordinare la colonna Mantra:
// per giocatori con multi-ruolo confronto primary role, poi secondary, ecc.
// User spec (iter 58): P<DC<DD<DS<B<E<M<C<W<T<A<PC
const MANTRA_ORDER = { POR: 0, P: 0, DC: 1, DD: 2, DS: 3, B: 4, E: 5, M: 6, C: 7, W: 8, T: 9, A: 10, PC: 11 };

function mantraSortKey(rolesArr) {
  const arr = splitMantraTokens(rolesArr || []);
  if (arr.length === 0) return "99";
  return arr.map(t => String(MANTRA_ORDER[String(t).toUpperCase()] ?? 99).padStart(2, "0")).join(".");
}

function splitMantraTokens(arr) {
  if (!Array.isArray(arr)) return [];
  const out = [];
  for (const raw of arr) {
    const tok = String(raw || "").trim().toUpperCase();
    if (!tok) continue;
    if (_VALID_MANTRA_MULTI.has(tok) || tok.length === 1) { out.push(tok); continue; }
    // Greedy split: prova prefissi 3 (POR) → 2 (DC/DD/DS/PC) → 1
    let rem = tok;
    const buf = [];
    let ok = true;
    while (rem) {
      let matched = false;
      for (const L of [3, 2, 1]) {
        const head = rem.slice(0, L);
        if (_VALID_MANTRA_MULTI.has(head) || (L === 1 && _VALID_MANTRA_SINGLE.has(head))) {
          buf.push(head); rem = rem.slice(L); matched = true; break;
        }
      }
      if (!matched) { ok = false; break; }
    }
    if (ok) out.push(...buf);
    else out.push(tok); // fallback: token non riconoscibile, meglio grigio che perdere info
  }
  return out;
}

const STATUS_BADGES = {
  attivo: { emoji: "✅", label: "Attivo", cls: "bg-slate-700/40 text-slate-300 border-slate-600" },
  blindato: { emoji: "🔒", label: "Blindato", cls: "bg-rose-500/20 text-rose-300 border-rose-500/40" },
  cedibile: { emoji: "🏷️", label: "Cedibile", cls: "bg-amber-500/20 text-amber-300 border-amber-500/40" },
  svincolato: { emoji: "🚪", label: "Svincolato", cls: "bg-slate-700/40 text-slate-400 border-slate-600" },
  ceduto_esterno: { emoji: "✈️", label: "Ceduto", cls: "bg-purple-500/20 text-purple-300 border-purple-500/40" },
};

// Iter 61: badge listing_status (SEPARATO dallo status di proprietà)
const LISTING_BADGES = {
  LISTED_ACTIVE: null, // nessun badge, situazione normale
  LISTED_CEDUTO: { emoji: "🏳️", label: "Ceduto (Serie A)", cls: "bg-orange-500/20 text-orange-300 border-orange-500/40", tip: "Giocatore ceduto dal club Serie A (foglio Ceduti). Proprietà fantasy conservata." },
  TEMPORARILY_UNLISTED: { emoji: "🚫", label: "Fuori listone", cls: "bg-slate-600/30 text-slate-300 border-slate-500/60", tip: "Assente dal listone ufficiale (senza contratto?). Proprietà conservata, valore congelato all'ultima quotazione valida." },
  RELISTED: { emoji: "✨", label: "Rilistato", cls: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40", tip: "Rientrato nel listone dopo un'assenza. Nuova quotazione applicata." },
};

const ROLE_LABEL_FULL = { P: "Portieri", D: "Difensori", C: "Centrocampisti", A: "Attaccanti" };

function getValueTier(rankInfo) {
  if (!rankInfo || !rankInfo.rank) return null;
  const { rank, total, role } = rankInfo;
  const roleLabel = ROLE_LABEL_FULL[role] || role;
  if (rank === 1) return {
    stars: "⭐⭐⭐", cls: "bg-amber-500/20 text-amber-200 border-amber-500/50",
    title: `TOP ${roleLabel} #1 in lega`,
  };
  if (rank <= 3) return {
    stars: "⭐⭐", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40",
    title: `TOP ${roleLabel} #${rank} in lega`,
  };
  // Top 10% of role (min 5) get single star
  const cutoff = Math.max(5, Math.floor(total * 0.1));
  if (rank <= cutoff) return {
    stars: "⭐", cls: "bg-slate-800 text-amber-400/80 border-amber-500/25",
    title: `Top ${roleLabel}: #${rank} in lega su ${total}`,
  };
  return null;
}

export default function LeagueRosa() {
  const { league, teams, myTeam, isAdmin } = useLeague();
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teamFilter, setTeamFilter] = useState(myTeam?.team_id || "");
  const [roleFilter, setRoleFilter] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ field: "mantra", dir: "asc" });  // iter 58: sort excel-like
  // iter 58: Filtri avanzati (multi-select ruoli mantra + range valore/roi + checkbox stato)
  const [advOpen, setAdvOpen] = useState(false);
  const [advMantra, setAdvMantra] = useState(new Set());     // Set<string> tokens Mantra da mostrare
  const [advValMin, setAdvValMin] = useState("");            // Valore minimo (delta QA-QI)
  const [advValMax, setAdvValMax] = useState("");
  const [advRoiMin, setAdvRoiMin] = useState("");            // ROI% minimo
  const [advRoiMax, setAdvRoiMax] = useState("");
  const [advStatus, setAdvStatus] = useState({ attivo: true, blindato: true, svincolato: true });
  const [transferPlayer, setTransferPlayer] = useState(null);
  const [roosterMap, setRoosterMap] = useState({}); // { player_name: rooster_id }
  const [attachTarget, setAttachTarget] = useState(null); // {name} of live player to attach
  const [valueRankMap, setValueRankMap] = useState({}); // { player_id: { rank, role, total } }
  const [bulkOpen, setBulkOpen] = useState(false); // Iter 62: dialog bulk resolve

  // Rooster candidate search (for attach modal)
  const [attachSearch, setAttachSearch] = useState("");
  const [attachResults, setAttachResults] = useState([]);
  const [attachBusy, setAttachBusy] = useState(false);
  useEffect(() => {
    if (!attachTarget) return;
    const t = setTimeout(async () => {
      const q = (attachSearch || attachTarget.name || "").trim();
      if (!q) { setAttachResults([]); return; }
      try {
        const r = await api.get(`/magnati/rooster?q=${encodeURIComponent(q)}&limit=20`);
        setAttachResults(r.data?.players || r.data || []);
      } catch (_) { setAttachResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [attachSearch, attachTarget]);

  async function doAttach(roosterId) {
    if (!attachTarget || !roosterId) return;
    setAttachBusy(true);
    try {
      const r = await api.post(`/magnati/rooster/attach-player`, {
        live_player_name: attachTarget.name, rooster_id: roosterId,
      });
      toast.success(`'${attachTarget.name}' collegato a ${r.data.canonical_name}`);
      // Update local rooster map so the badge appears immediately
      setRoosterMap(m => ({ ...m, [attachTarget.name]: roosterId }));
      setAttachTarget(null);
      setAttachSearch("");
      setAttachResults([]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAttachBusy(false); }
  }

  const load = async (tid = teamFilter) => {
    setLoading(true);
    try {
      const params = {};
      if (tid) params.team_id = tid;
      const r = await api.get(`/leagues/${league.league_id}/players`, { params });
      setPlayers(r.data);
      // Resolve rooster IDs for all loaded player names (best-effort, non-blocking)
      if (r.data?.length) {
        const uniqueNames = Array.from(new Set(r.data.map(p => p.name).filter(Boolean)));
        api.post("/magnati/rooster/resolve-batch", { names: uniqueNames })
          .then(res => setRoosterMap(res.data?.map || {}))
          .catch(() => { /* silent */ });
      }
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(teamFilter); }, [teamFilter, league.league_id]);

  // Compute value rank per role across the ENTIRE league (independent of team filter)
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/leagues/${league.league_id}/players`);
        const all = r.data || [];
        // Group by role, sort desc by valore_investimento (fallback quotazione_attuale)
        const byRole = { P: [], D: [], C: [], A: [] };
        for (const p of all) {
          const role = (p.role || "").toUpperCase();
          if (byRole[role]) byRole[role].push(p);
        }
        const map = {};
        for (const role of Object.keys(byRole)) {
          const list = byRole[role].sort((a, b) => {
            const va = Number(a.quotazione_attuale || 0);
            const vb = Number(b.quotazione_attuale || 0);
            return vb - va;
          });
          list.forEach((p, idx) => {
            map[p.player_id] = { rank: idx + 1, total: list.length, role };
          });
        }
        setValueRankMap(map);
      } catch (_) { /* silent */ }
    })();
  }, [league.league_id, players.length]);

  const filtered = useMemo(() => {
    const base = players.filter(p => {
      if (roleFilter && p.role !== roleFilter) return false;
      if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
      // iter 58: filtri avanzati
      if (advMantra.size > 0) {
        const tokens = splitMantraTokens(p.roles_mantra || []).map(t => t.toUpperCase());
        if (!tokens.some(t => advMantra.has(t))) return false;
      }
      const val = Number(p.valore_investimento || 0);
      if (advValMin !== "" && val < Number(advValMin)) return false;
      if (advValMax !== "" && val > Number(advValMax)) return false;
      const roi = Number(p.roi_pct || 0);
      if (advRoiMin !== "" && roi < Number(advRoiMin)) return false;
      if (advRoiMax !== "" && roi > Number(advRoiMax)) return false;
      const st = String(p.status || "attivo");
      if (!advStatus[st]) return false;
      return true;
    });
    // Sort come Excel: dir 'asc' o 'desc', chiave selezionata da sort.field
    const dir = sort.dir === "desc" ? -1 : 1;
    const num = (v) => Number(v || 0);
    const key = (p) => {
      switch (sort.field) {
        case "role": return String(p.role || "z");
        case "mantra": return mantraSortKey(p.roles_mantra);
        case "name": return String(p.name || "").toLowerCase();
        case "club": return String(p.real_team || "z").toLowerCase();
        case "team": return String(teams.find(t => t.team_id === p.team_id)?.name || "z").toLowerCase();
        case "qi": return num(p.quotazione_iniziale);
        case "qa": return num(p.quotazione_attuale);
        case "fvm": return num(p.fvm);
        case "paid": return num(p.prezzo_pagato);
        case "value": return num(p.valore_investimento);
        case "roi": return num(p.roi_pct);
        case "salary": return num(p.salary);
        case "status": return String(p.status || "z");
        default: return 0;
      }
    };
    return [...base].sort((a, b) => {
      const ka = key(a), kb = key(b);
      if (ka < kb) return -1 * dir;
      if (ka > kb) return 1 * dir;
      // Tiebreak stabile su nome per ordinamento consistente
      return String(a.name || "").localeCompare(String(b.name || ""));
    });
  }, [players, roleFilter, search, sort, teams, advMantra, advValMin, advValMax, advRoiMin, advRoiMax, advStatus]);

  const advActiveCount = (advMantra.size > 0 ? 1 : 0) + (advValMin !== "" || advValMax !== "" ? 1 : 0) + (advRoiMin !== "" || advRoiMax !== "" ? 1 : 0) + (Object.values(advStatus).some(v => !v) ? 1 : 0);
  const clearAdvFilters = () => {
    setAdvMantra(new Set());
    setAdvValMin(""); setAdvValMax("");
    setAdvRoiMin(""); setAdvRoiMax("");
    setAdvStatus({ attivo: true, blindato: true, svincolato: true });
  };

  const toggleSort = (field) => {
    setSort(s => s.field === field ? { field, dir: s.dir === "asc" ? "desc" : "asc" } : { field, dir: "asc" });
  };
  const sortArrow = (field) => {
    if (sort.field !== field) return <span className="opacity-30 ml-0.5">↕</span>;
    return <span className="text-amber-400 ml-0.5">{sort.dir === "asc" ? "↑" : "↓"}</span>;
  };

  const totals = useMemo(() => {
    // Iter 63: `valore_riv` = somma dei valori rivalutati (pp × QA/QI).
    // `delta` = valore_riv − paid (plusvalenza/minusvalenza aggregata).
    const tot = { paid: 0, qa: 0, valore_riv: 0, delta: 0, salary: 0, base_asta: 0 };
    for (const p of filtered) {
      tot.paid += Number(p.prezzo_pagato || 0);
      tot.qa += Number(p.quotazione_attuale || 0);
      tot.valore_riv += Number(p.valore_investimento || 0);
      tot.salary += Number(p.salary || 0);
      tot.base_asta += Number(p.base_asta_furti || 0);
    }
    tot.delta = tot.valore_riv - tot.paid;
    return tot;
  }, [filtered]);

  const teamName = (tid) => teams.find(t => t.team_id === tid)?.name || "—";

  return (
    <div className="space-y-4">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3">
        <div>
          <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold">⚽ Rosa</div>
          <h2 className="text-3xl font-black tracking-tight">{teamFilter ? teamName(teamFilter) : "Tutte le rose"}</h2>
        </div>
        <div className="flex flex-wrap gap-2">
          <select value={teamFilter} onChange={(e)=>setTeamFilter(e.target.value)}
            className="bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            data-testid="rosa-team-filter">
            <option value="">🌐 Tutte le squadre</option>
            {teams.map(t => <option key={t.team_id} value={t.team_id}>⚽ {t.name}</option>)}
          </select>
          <select value={roleFilter} onChange={(e)=>setRoleFilter(e.target.value)}
            className="bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
            data-testid="rosa-role-filter">
            <option value="">Tutti i ruoli</option>
            <option value="P">🥅 Portieri</option>
            <option value="D">🛡️ Difensori</option>
            <option value="C">⚙️ Centrocampisti</option>
            <option value="A">🎯 Attaccanti</option>
          </select>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Cerca giocatore…"
              className="pl-8 bg-slate-950 border border-slate-800 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              data-testid="rosa-search" />
          </div>
          {/* iter 58: Filtri avanzati */}
          <button
            onClick={() => setAdvOpen(v => !v)}
            data-testid="rosa-adv-filters-btn"
            className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-black uppercase transition-all border ${advActiveCount > 0 ? "border-amber-500/60 bg-amber-500/10 text-amber-300" : "border-slate-700 hover:border-amber-500/60 text-slate-300"}`}
            title="Filtri avanzati"
          >
            <Filter size={14}/> Filtri
            {advActiveCount > 0 && <span className="px-1.5 py-0.5 rounded-full bg-amber-500 text-slate-950 text-[10px] leading-none font-black">{advActiveCount}</span>}
          </button>
          <button
            onClick={() => {
              // Iter 58: Export CSV client-side che rispetta sort + filtri correnti
              const cols = [
                ["ruolo", p => p.role || ""],
                ["nome", p => p.name || ""],
                ["mantra", p => (splitMantraTokens(p.roles_mantra || []).join(";"))],
                ["club", p => p.real_team || ""],
                ["squadra", p => teams.find(t => t.team_id === p.team_id)?.name || ""],
                ["quotazione_iniziale", p => p.quotazione_iniziale ?? ""],
                ["quotazione_attuale", p => p.quotazione_attuale ?? ""],
                ["fvm", p => p.fvm ?? ""],
                ["prezzo_pagato", p => p.prezzo_pagato ?? ""],
                ["valore", p => p.valore_investimento ?? ""],
                ["roi_pct", p => p.roi_pct ?? ""],
                ["salary", p => p.salary ?? ""],
                ["status", p => p.status || ""],
              ];
              const escape = v => {
                const s = String(v ?? "");
                return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
              };
              const header = cols.map(c => c[0]).join(";");
              const rows = filtered.map(p => cols.map(c => escape(c[1](p))).join(";"));
              const csv = "\uFEFF" + [header, ...rows].join("\n");  // BOM per Excel
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              const suffix = `${filtered.length}pl_${sort.field}${sort.dir === "desc" ? "-desc" : ""}`;
              a.href = url;
              a.download = `rosa_${league.league_id}_${suffix}.csv`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
              toast.success(`📥 ${filtered.length} giocatori esportati (sort: ${sort.field} ${sort.dir})`);
            }}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-2xl border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-black uppercase transition-all hover:scale-105"
            data-testid="rosa-export-csv" title="Esporta rose (ordinamento + filtri correnti)">
            📥 CSV{advActiveCount > 0 || sort.field !== "mantra" ? " ✂" : ""}
          </button>
        </div>
      </div>

      {/* iter 58: Advanced filters popover — multi-select Mantra + range Valore/ROI + status */}
      {advOpen && (
        <div className="rounded-xl border border-amber-500/40 bg-slate-900/80 p-4 space-y-3" data-testid="rosa-adv-filters-panel">
          <div className="flex items-center justify-between">
            <div className="text-sm font-black uppercase tracking-widest text-amber-300 flex items-center gap-1.5">
              <Filter size={14}/> Filtri avanzati
            </div>
            <button onClick={clearAdvFilters} data-testid="rosa-adv-clear" className="text-xs text-slate-400 hover:text-rose-300 underline decoration-dotted">Azzera tutto</button>
          </div>

          {/* Multi-select Mantra */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Ruoli Mantra (multi)</div>
            <div className="flex flex-wrap gap-1.5">
              {["POR","DC","DD","DS","B","E","M","C","W","T","A","PC"].map(tok => {
                const active = advMantra.has(tok);
                const st = mantraStyle(tok);
                return (
                  <button key={tok} onClick={() => {
                    setAdvMantra(prev => { const n = new Set(prev); if (n.has(tok)) n.delete(tok); else n.add(tok); return n; });
                  }} data-testid={`rosa-adv-mantra-${tok}`}
                    className={`px-2 py-1 rounded-md text-[11px] font-black uppercase tracking-wider transition-all ${active ? `${st.bg} ${st.text} ring-2 ring-white/50` : "border border-slate-700 text-slate-400 hover:border-slate-500"}`}
                  >{tok}</button>
                );
              })}
            </div>
          </div>

          {/* Range Valore + ROI */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Valore rivalutato (pp × QA/QI) range</div>
              <div className="flex items-center gap-1.5">
                <input type="number" value={advValMin} onChange={e => setAdvValMin(e.target.value)} placeholder="min" data-testid="rosa-adv-val-min" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm focus:border-amber-500 outline-none"/>
                <span className="text-slate-500">→</span>
                <input type="number" value={advValMax} onChange={e => setAdvValMax(e.target.value)} placeholder="max" data-testid="rosa-adv-val-max" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm focus:border-amber-500 outline-none"/>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">ROI % range</div>
              <div className="flex items-center gap-1.5">
                <input type="number" value={advRoiMin} onChange={e => setAdvRoiMin(e.target.value)} placeholder="min" data-testid="rosa-adv-roi-min" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm focus:border-amber-500 outline-none"/>
                <span className="text-slate-500">→</span>
                <input type="number" value={advRoiMax} onChange={e => setAdvRoiMax(e.target.value)} placeholder="max" data-testid="rosa-adv-roi-max" className="w-full bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm focus:border-amber-500 outline-none"/>
              </div>
            </div>
          </div>

          {/* Status checkboxes */}
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1.5">Stato giocatore</div>
            <div className="flex flex-wrap gap-3">
              {["attivo","blindato","svincolato"].map(st => (
                <label key={st} className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="checkbox" checked={advStatus[st]}
                    onChange={e => setAdvStatus(s => ({...s, [st]: e.target.checked}))}
                    data-testid={`rosa-adv-status-${st}`}
                    className="accent-amber-500"/>
                  <span className="capitalize">{st}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <SummaryCard emoji="👥" label="Giocatori" v={filtered.length} />
        <SummaryCard emoji="💸" label="Valore Acquisti" v={fmtMoney(totals.paid) + " cr"} subtitle="somma prezzo pagato" />
        <SummaryCard emoji="📊" label="Valore Rosa (QA)" v={fmtMoney(totals.qa) + " cr"} accent="text-cyan-300" subtitle="somma quotazioni attuali" />
        <SummaryCard emoji="💎" label="Valore rivalutato" v={fmtMoney(totals.valore_riv) + " cr"}
          accent={totals.delta >= 0 ? "text-emerald-400" : "text-rose-400"}
          subtitle={`${totals.delta >= 0 ? "+" : ""}${fmtMoney(totals.delta)} cr vs pagato · pp × QA/QI`} />
        <SummaryCard emoji="🎯" label="Base asta furti tot." v={fmtMoney(totals.base_asta) + " cr"} accent="text-amber-400"
          subtitle="valore + bonus stipendio"/>
        <SummaryCard emoji="💵" label="Monte stipendi" v={fmtMoney(totals.salary) + " cr"} accent="text-rose-400"/>
      </div>

      {/* Iter 68: Salute Rosa spostata in Decisioni Admin → /lega/{id}/salute-rosa */}

      {/* Iter 65: Widget Crediti Prossima Asta (per il team correntemente selezionato) */}
      {teamFilter && (() => {
        const team = teams.find(t => t.team_id === teamFilter);
        if (!team) return null;
        const budget = Number(team.budget || 0);
        const monteStip = totals.salary;
        const available = budget - monteStip;
        const stipPct = budget > 0 ? (monteStip / budget * 100) : 0;
        return (
          <div className="rounded-3xl border border-emerald-500/40 bg-gradient-to-br from-emerald-950/40 to-slate-950 p-4" data-testid="rosa-credits-widget">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-500/50 flex items-center justify-center text-2xl">💰</div>
                <div>
                  <div className="text-[10px] tracking-[0.3em] text-emerald-300 uppercase font-bold">Crediti per Prossima Asta</div>
                  <div className="text-xs text-slate-400">Budget − monte stipendi (aggiornato in tempo reale)</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-emerald-300 font-mono leading-none" data-testid="credits-available-value">{fmtMoney(available)}<span className="text-lg text-emerald-400/60 ml-1">cr</span></div>
                <div className="text-xs text-slate-500 mt-0.5">disponibili</div>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="p-2 rounded-xl bg-slate-950/60 border border-slate-800">
                <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Budget</div>
                <div className="text-base font-black font-mono text-amber-300 mt-0.5">{fmtMoney(budget)}<span className="text-[10px] text-amber-500/60 ml-0.5">cr</span></div>
              </div>
              <div className="p-2 rounded-xl bg-slate-950/60 border border-slate-800">
                <div className="text-[9px] uppercase tracking-widest text-slate-500 font-bold">Stipendi</div>
                <div className="text-base font-black font-mono text-rose-400 mt-0.5">−{fmtMoney(monteStip)}<span className="text-[10px] text-rose-500/60 ml-0.5">cr</span></div>
                <div className="text-[9px] text-slate-500 mt-0.5">({stipPct.toFixed(0)}%)</div>
              </div>
              <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/40">
                <div className="text-[9px] uppercase tracking-widest text-emerald-400 font-bold">Disponibili</div>
                <div className="text-base font-black font-mono text-emerald-300 mt-0.5">{fmtMoney(available)}<span className="text-[10px] text-emerald-500/60 ml-0.5">cr</span></div>
                <div className="text-[9px] text-emerald-500 mt-0.5">= {((available / budget) * 100).toFixed(0)}%</div>
              </div>
            </div>
          </div>
        );
      })()}

      {loading ? (
        <div className="text-slate-500 py-10 text-center">Caricamento…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-slate-800 rounded-lg p-10 text-center text-slate-500">
          <div className="text-4xl mb-2">😴</div>
          Nessun giocatore trovato. Importa la rosa dalla sezione <strong className="text-amber-400">Import</strong>.
        </div>
      ) : (
        <div className="border border-slate-800 rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 overflow-hidden shadow-2xl shadow-black/40">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-slate-950/70">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-3 py-3 cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("role")} data-testid="sort-role">R{sortArrow("role")}</th>
                <th className="px-3 py-3 cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("name")} data-testid="sort-name">Nome{sortArrow("name")}</th>
                <th className="px-3 py-3 cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("mantra")} data-testid="sort-mantra">Mantra{sortArrow("mantra")}</th>
                <th className="px-3 py-3 cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("club")} data-testid="sort-club">Club{sortArrow("club")}</th>
                {!teamFilter && <th className="px-3 py-3 cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("team")} data-testid="sort-team">Squadra{sortArrow("team")}</th>}
                <th className="px-3 py-3 text-right cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("qi")} data-testid="sort-qi">Qt.I{sortArrow("qi")}</th>
                <th className="px-3 py-3 text-right cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("qa")} data-testid="sort-qa">Qt.A{sortArrow("qa")}</th>
                <th className="px-3 py-3 text-right cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("fvm")} data-testid="sort-fvm">💠 FVM{sortArrow("fvm")}</th>
                <th className="px-3 py-3 text-right cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("paid")} data-testid="sort-paid">💸 Pagato{sortArrow("paid")}</th>
                <th className="px-3 py-3 text-right cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("value")} data-testid="sort-value">💎 Valore{sortArrow("value")}</th>
                <th className="px-3 py-3 text-right hover:text-slate-200 transition-colors" title="Base asta furti = valore rivalutato + bonus stipendio (10/20/30/40)">🎯 Base Asta</th>
                <th className="px-3 py-3 text-right cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("roi")} data-testid="sort-roi">📊 ROI{sortArrow("roi")}</th>
                <th className="px-3 py-3 text-right cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("salary")} data-testid="sort-salary">💵 Stip.{sortArrow("salary")}</th>
                <th className="px-3 py-3 cursor-pointer select-none hover:text-slate-200 transition-colors" onClick={() => toggleSort("status")} data-testid="sort-status">Stato{sortArrow("status")}</th>
                {isAdmin && <th className="px-3 py-3 text-right" title="Azioni admin">⚙️</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const roi = Number(p.roi_pct || 0);
                const st = STATUS_BADGES[p.status] || STATUS_BADGES.attivo;
                const listing = LISTING_BADGES[p.listing_status] || null;
                const mantra = splitMantraTokens(p.roles_mantra || []);
                return (
                  <tr key={p.player_id} className="border-t border-slate-800 hover:bg-slate-800/40 hover:shadow-inner transition-colors" data-testid={`player-row-${p.player_id}`}>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-black text-[12px] border shadow-md ${ROLE_STYLES[p.role] || "bg-slate-700"}`}>{p.role}</span>
                    </td>
                    <td className="px-3 py-2.5 font-bold">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {(() => {
                          const tier = getValueTier(valueRankMap[p.player_id]);
                          return tier ? (
                            <span data-testid={`value-tier-${p.player_id}`}
                              title={tier.title}
                              className={`inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] border shrink-0 ${tier.cls}`}>
                              {tier.stars}
                            </span>
                          ) : null;
                        })()}
                        {p.fanta_id ? (
                          <PlayerNameLink
                            name={p.name}
                            fantaId={p.fanta_id}
                            roosterMap={roosterMap}
                            testid={`player-history-link-${p.player_id}`}
                            className="font-inherit"/>
                        ) : roosterMap[p.name] ? (
                          <Link
                            to={`/storico-magnati/giocatore/${roosterMap[p.name]}`}
                            title="Apri storia carriera (squadre, prezzi, trasferimenti)"
                            data-testid={`player-history-link-${p.player_id}`}
                            className="hover:text-amber-300 underline decoration-amber-500/30 decoration-dotted underline-offset-4 transition-colors">
                            {p.name}
                          </Link>
                        ) : isAdmin ? (
                          <button
                            type="button"
                            onClick={() => setAttachTarget({ name: p.name })}
                            title="Nome non ancora collegato allo storico. Clicca per collegarlo."
                            data-testid={`player-attach-name-${p.player_id}`}
                            className="hover:text-purple-300 underline decoration-purple-500/30 decoration-dotted underline-offset-4 transition-colors">
                            {p.name}
                          </button>
                        ) : (
                          <span>{p.name}</span>
                        )}
                        {listing && (
                          <span
                            data-testid={`listing-badge-${p.player_id}`}
                            title={listing.tip}
                            className={`text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 font-semibold ${listing.cls}`}>
                            {listing.emoji} {listing.label}
                          </span>
                        )}
                        {p.in_rose_file === false && (
                          <span
                            data-testid={`not-in-rose-file-${p.player_id}`}
                            title="Non presente nell'ultimo file rose Fantacalcio.it importato. Verifica manualmente la proprietà."
                            className="text-[10px] px-1.5 py-0.5 rounded-full border shrink-0 font-semibold bg-amber-500/15 text-amber-300 border-amber-500/40">
                            🔎 Da verificare
                          </span>
                        )}
                        {p.is_loan && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/40">🔄 P{p.loan_duration}</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-wrap gap-1.5 items-center" data-testid={`mantra-cell-${p.player_id}`}>
                        {mantra.length > 0 ? mantra.map((tok, i) => {
                          const st = mantraStyle(tok);
                          return (
                            <span key={i}
                              title={st.label}
                              className={`inline-flex items-center justify-center min-w-[28px] h-7 px-1.5 rounded-full text-[10px] font-black tracking-wider ${st.bg} ${st.text} shadow-md ring-1 ${st.ring} ring-opacity-40`}>
                              {String(tok).toUpperCase()}
                            </span>
                          );
                        }) : <span className="text-slate-600 text-xs">—</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-slate-400">{p.real_team || "—"}</td>
                    {!teamFilter && <td className="px-3 py-2.5 text-slate-300">{teamName(p.team_id)}</td>}
                    <td className="px-3 py-2.5 text-right font-mono text-slate-400">{fmtMoney(p.quotazione_iniziale)}</td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      <div className="inline-flex items-center gap-1.5 justify-end">
                        <span>{fmtMoney(p.quotazione_attuale)}</span>
                        {(() => {
                          const d = Number(p.qa_delta_last || 0);
                          if (!d) return null;
                          return (
                            <span title={`Ultimo scostamento: ${d > 0 ? "+" : ""}${d}`}
                              data-testid={`qa-delta-${p.player_id}`}
                              className={`text-[10px] font-black px-1 py-0.5 rounded ${d > 0 ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "bg-rose-500/15 text-rose-300 border border-rose-500/40"}`}>
                              {d > 0 ? "↗" : "↘"} {d > 0 ? "+" : ""}{d}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">
                      {p.fvm ? (
                        <div className="inline-flex items-center gap-1.5 justify-end">
                          <span className="text-cyan-300 font-bold">{fmtMoney(p.fvm)}</span>
                          {(() => {
                            const d = Number(p.fvm_delta_last || 0);
                            if (!d) return null;
                            return (
                              <span title={`Delta FVM: ${d > 0 ? "+" : ""}${d}`}
                                data-testid={`fvm-delta-${p.player_id}`}
                                className={`text-[9px] font-black px-1 py-0.5 rounded ${d > 0 ? "text-emerald-300" : "text-rose-300"}`}>
                                {d > 0 ? "↗" : "↘"}
                              </span>
                            );
                          })()}
                        </div>
                      ) : <span className="text-slate-700">—</span>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{fmtMoney(p.prezzo_pagato)}</td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold ${Number(p.valore_investimento || 0) >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {fmtMoney(p.valore_investimento)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold text-amber-300" title={`Valore ${fmtMoney(p.valore_investimento)} + Bonus stip. ${p.bonus_stipendio_cr || 0}cr (${p.bonus_stipendio_pct || 0}%)`} data-testid={`base-asta-${p.player_id}`}>
                      {fmtMoney(p.base_asta_furti)}
                      {(p.bonus_stipendio_pct || 0) > 0 && (
                        <span className="text-[9px] text-amber-500/80 ml-1">+{p.bonus_stipendio_pct}%</span>
                      )}
                    </td>
                    <td className={`px-3 py-2.5 text-right font-mono font-bold ${roi >= 0 ? "text-emerald-400" : "text-rose-400"}`}>
                      {roi >= 0 ? "↗" : "↘"} {roi.toFixed(1)}%
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono text-slate-300">{fmtMoney(p.salary)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border ${st.cls}`}>{st.emoji} {st.label}{p.renewal_level > 0 && ` L${p.renewal_level}`}</span>
                    </td>
                    {isAdmin && (
                      <td className="px-3 py-2.5 text-right">
                        <button onClick={()=>setTransferPlayer(p)} data-testid={`transfer-btn-${p.player_id}`}
                          className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full border border-purple-500/40 text-purple-300 hover:bg-purple-500/20 transition-all hover:scale-105"
                          title="Ha cambiato squadra?">
                          <ArrowRightLeft size={10}/> Trasferisci
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {transferPlayer && (
        <TransferModal
          player={transferPlayer}
          teams={teams}
          leagueId={league.league_id}
          onClose={()=>setTransferPlayer(null)}
          onDone={async ()=>{ setTransferPlayer(null); await load(teamFilter); }}
        />
      )}

      {/* Attach History Modal (admin only) */}
      {attachTarget && (
        <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !attachBusy && setAttachTarget(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-gradient-to-br from-slate-900 to-slate-950 border border-purple-500/30 shadow-2xl" onClick={e => e.stopPropagation()} data-testid="attach-history-modal">
            <div className="p-5 border-b border-slate-800">
              <div className="text-xs font-mono uppercase tracking-widest text-purple-400/80 flex items-center gap-2">
                <Link2 className="w-3.5 h-3.5"/> Collega alla Storia
              </div>
              <div className="text-lg font-bold text-slate-100 mt-1">{attachTarget.name}</div>
              <div className="text-xs text-slate-500 mt-1">Cerca il giocatore corretto nell&apos;archivio storico. Il nome verrà aggiunto come alias per matchare i futuri import.</div>
            </div>
            <div className="p-5 space-y-3">
              <input type="text" value={attachSearch} onChange={e => setAttachSearch(e.target.value)} autoFocus
                placeholder={`Cerca (default: '${attachTarget.name}')…`}
                data-testid="attach-search-input"
                className="w-full bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-100 placeholder:text-slate-600"/>
              <div className="max-h-72 overflow-y-auto border border-slate-800 rounded-lg" data-testid="attach-results-list">
                {attachResults.length === 0 && (
                  <div className="p-4 text-xs text-slate-500 text-center italic">Nessun risultato. Prova a modificare la ricerca.</div>
                )}
                {attachResults.map((r, i) => (
                  <button key={r.id} onClick={() => doAttach(r.id)} disabled={attachBusy}
                    data-testid={`attach-candidate-${i}`}
                    className="w-full text-left px-3 py-2.5 border-b border-slate-800 last:border-b-0 hover:bg-purple-500/10 disabled:opacity-50 transition-colors">
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-100">{r.canonical_name}</span>
                      <span className="text-[10px] font-mono text-slate-500 uppercase">{r.current_ruolo}</span>
                      <span className="text-[10px] font-mono text-slate-500 uppercase">{r.current_squadra_a}</span>
                      <span className="ml-auto text-[10px] text-slate-400 font-mono">{(r.career?.length ?? r.total_seasons ?? 0)} stag.</span>
                    </div>
                    {(r.aliases?.length || 0) > 1 && (
                      <div className="text-[10px] text-slate-500 mt-0.5 truncate">alias: {(r.aliases || []).join(", ")}</div>
                    )}
                  </button>
                ))}
              </div>
              <div className="text-[10px] text-slate-500 italic">
                Suggerimento: se un giocatore ha cambiato nome (es. &quot;Doig&quot; → &quot;Doig B.&quot;) selezionalo qui e nel prossimo import il match sarà automatico.
              </div>
              <button onClick={() => setAttachTarget(null)} disabled={attachBusy}
                data-testid="close-attach-modal-btn"
                className="w-full px-4 py-2 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase tracking-wider">
                Annulla
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter 62: Bulk Resolve Fanta ID Dialog (admin only) */}
      {bulkOpen && (
        <BulkResolveDialog
          leagueId={league.league_id}
          onClose={() => setBulkOpen(false)}
          onDone={async () => { setBulkOpen(false); await load(teamFilter); }}
        />
      )}
    </div>
  );
}

function TransferModal({ player, teams, leagueId, onClose, onDone }) {
  const [hasChanged, setHasChanged] = useState(null); // true = interna, false = fuori lega
  const [toTeamId, setToTeamId] = useState("");
  const [amount, setAmount] = useState(player.prezzo_pagato || 0);
  const [newSalary, setNewSalary] = useState(player.salary || 0);
  const [externalNote, setExternalNote] = useState("");
  const [allowOverdraft, setAllowOverdraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const others = teams.filter(t => t.team_id !== player.team_id);
  const targetTeam = teams.find(t => t.team_id === toTeamId);
  const targetBudgetAfter = targetTeam ? Number(targetTeam.budget || 0) - Number(amount || 0) : null;

  const submit = async () => {
    if (hasChanged === null) { toast.error("Rispondi alla domanda"); return; }
    if (hasChanged && !toTeamId) { toast.error("Scegli la squadra destinataria"); return; }
    setSubmitting(true);
    try {
      const body = hasChanged
        ? { to_team_id: toTeamId, amount: Number(amount || 0), new_salary: Number(newSalary || 0), allow_overdraft: allowOverdraft }
        : { to_team_id: null, amount: Number(amount || 0), external_note: externalNote };
      await api.post(`/leagues/${leagueId}/players/${player.player_id}/transfer`, body);
      toast.success(hasChanged ? "🔀 Trasferimento registrato" : "✈️ Giocatore ceduto fuori lega");
      await onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e=>e.stopPropagation()}
        className="w-full max-w-lg rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800 shadow-2xl shadow-black/80 p-6 space-y-4"
        data-testid="transfer-modal">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs tracking-[0.3em] text-purple-400 uppercase font-bold">🔀 Trasferimento manuale</div>
            <h3 className="text-2xl font-black mt-1">Ha cambiato squadra?</h3>
            <div className="text-sm text-slate-400 mt-1">
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full font-black text-[10px] border ${ROLE_STYLES[player.role] || "bg-slate-700"} mr-1`}>{player.role}</span>
              <span className="font-bold">{player.name}</span> · {player.real_team} · pagato {fmtMoney(player.prezzo_pagato)} cr
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white p-1" data-testid="transfer-close">
            <X size={20}/>
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button onClick={()=>setHasChanged(true)} data-testid="transfer-yes"
            className={`px-4 py-3 rounded-2xl border-2 font-bold uppercase text-sm transition-all ${
              hasChanged === true ? "border-emerald-400 bg-emerald-500/20 text-emerald-300 shadow-lg shadow-emerald-500/20" : "border-slate-800 text-slate-400 hover:border-emerald-500/50"
            }`}>
            ✅ Sì (interna)
          </button>
          <button onClick={()=>setHasChanged(false)} data-testid="transfer-external"
            className={`px-4 py-3 rounded-2xl border-2 font-bold uppercase text-sm transition-all ${
              hasChanged === false ? "border-purple-400 bg-purple-500/20 text-purple-300 shadow-lg shadow-purple-500/20" : "border-slate-800 text-slate-400 hover:border-purple-500/50"
            }`}>
            ✈️ Fuori lega
          </button>
        </div>

        {hasChanged === true && (
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">Nuova squadra</label>
              <select value={toTeamId} onChange={e=>setToTeamId(e.target.value)} data-testid="transfer-to-team"
                className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2.5 text-sm">
                <option value="">— seleziona —</option>
                {others.map(t => <option key={t.team_id} value={t.team_id}>{t.name}</option>)}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">💰 Costo (cr)</label>
                <input type="number" min="0" step="1" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="transfer-amount"
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2.5 text-sm font-mono"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">💵 Nuovo stip.</label>
                <input type="number" min="0" step="0.5" value={newSalary} onChange={e=>setNewSalary(e.target.value)} data-testid="transfer-new-salary"
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2.5 text-sm font-mono"/>
              </div>
            </div>
            <div className="text-[10px] text-slate-500 border-l-2 border-emerald-500 pl-2">
              💡 Il costo verrà <strong className="text-rose-300">scalato</strong> dalla nuova squadra e <strong className="text-emerald-300">accreditato</strong> alla vecchia. Nuovo stipendio applicato al giocatore.
            </div>
            {targetTeam && (
              <div className={`text-[11px] rounded-2xl border p-2 flex items-center justify-between ${targetBudgetAfter < 0 ? "border-rose-500/50 bg-rose-500/10" : "border-slate-700 bg-slate-800/40"}`}>
                <span className="text-slate-400">Budget {targetTeam.name} dopo:</span>
                <span className={`font-mono font-bold ${targetBudgetAfter < 0 ? "text-rose-300" : "text-emerald-300"}`}>{fmtMoney(targetBudgetAfter)} cr</span>
              </div>
            )}
            {targetBudgetAfter !== null && targetBudgetAfter < 0 && (
              <label className="flex items-center gap-2 text-[11px] text-rose-300 cursor-pointer" data-testid="transfer-overdraft">
                <input type="checkbox" checked={allowOverdraft} onChange={e=>setAllowOverdraft(e.target.checked)}/>
                ⚠️ Consenti comunque budget negativo per {targetTeam.name}
              </label>
            )}
          </div>
        )}

        {hasChanged === false && (
          <div className="space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">💰 Crediti recuperati</label>
              <input type="number" min="0" step="1" value={amount} onChange={e=>setAmount(e.target.value)} data-testid="transfer-external-amount"
                className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2.5 text-sm font-mono"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold block mb-1">📝 Nota (dove è finito)</label>
              <input value={externalNote} onChange={e=>setExternalNote(e.target.value)} data-testid="transfer-external-note"
                placeholder="Es: Ceduto a lega XYZ" className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-3 py-2.5 text-sm"/>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-2xl border border-slate-700 text-slate-400 hover:text-white text-sm font-bold uppercase" data-testid="transfer-cancel">
            Annulla
          </button>
          <button onClick={submit} disabled={submitting || hasChanged === null} data-testid="transfer-submit"
            className="px-6 py-2 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 text-white font-black uppercase text-sm shadow-lg shadow-purple-500/40 disabled:opacity-50">
            {submitting ? "Salvo…" : "🔀 Conferma"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ emoji, label, v, accent, subtitle }) {
  return (
    <div className="rounded-3xl border border-slate-800 bg-gradient-to-br from-slate-900 to-slate-950 p-4 shadow-xl shadow-black/40 hover:-translate-y-0.5 transition-all">
      <div className="text-[10px] tracking-widest text-slate-500 uppercase flex items-center gap-1">{emoji} {label}</div>
      <div className={`text-xl font-mono font-black mt-1 ${accent || "text-white"}`}>{v}</div>
      {subtitle && <div className={`text-[10px] font-mono mt-0.5 ${accent || "text-slate-400"}`}>{subtitle}</div>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ITER 62: BULK RESOLVE FANTA_ID DIALOG (admin)
// ═══════════════════════════════════════════════════════════════════

function BulkResolveDialog({ leagueId, onClose, onDone }) {
  const [loading, setLoading] = useState(true);
  const [unresolved, setUnresolved] = useState([]);
  const [selections, setSelections] = useState({}); // { player_id: fanta_id }
  const [applying, setApplying] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/leagues/${leagueId}/players/unresolved-suggestions`);
        setUnresolved(r.data.unresolved || []);
        // Preseleziona il candidato top se score >= 0.85
        const preselect = {};
        (r.data.unresolved || []).forEach(u => {
          const top = u.candidates?.[0];
          if (top && top.score >= 0.85) preselect[u.player_id] = top.fanta_id;
        });
        setSelections(preselect);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [leagueId]);

  const applyResolutions = async () => {
    const resolutions = Object.entries(selections)
      .filter(([, fid]) => !!fid)
      .map(([player_id, fanta_id]) => ({ player_id, fanta_id }));
    if (resolutions.length === 0) { toast.error("Nessuna selezione"); return; }
    if (!confirm(`Applicare ${resolutions.length} collegamenti fanta_id?`)) return;
    setApplying(true);
    try {
      const r = await api.post(`/leagues/${leagueId}/players/bulk-resolve-fanta-id`, { resolutions });
      toast.success(`✅ ${r.data.applied} collegamenti applicati`);
      await onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setApplying(false); }
  };

  const aiResolve = async () => {
    if (!confirm("🤖 Chiedere a Claude Sonnet 5 di collegare automaticamente tutti i giocatori? (Consuma credito AI)")) return;
    setAiBusy(true);
    try {
      const r = await api.post(`/leagues/${leagueId}/players/ai-resolve-unresolved`);
      toast.success(`🤖 ${r.data.applied} collegamenti AI applicati (su ${r.data.considered} considerati)`);
      await onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAiBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full max-w-5xl max-h-[90vh] rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-amber-500/40 shadow-2xl flex flex-col" data-testid="bulk-resolve-modal">
        <div className="p-5 border-b border-slate-800 flex items-center justify-between">
          <div>
            <div className="text-xs tracking-[0.3em] text-amber-400 uppercase font-bold">🩺 Salute Rosa · Bulk Resolve</div>
            <h3 className="text-2xl font-black mt-1">Collega giocatori al listone</h3>
            <div className="text-xs text-slate-400 mt-1">
              {unresolved.length} giocatori da risolvere. Scegli il candidato giusto per ciascuno oppure usa l&apos;AI.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={aiResolve} disabled={aiBusy || applying || loading} className="px-4 py-2 rounded-2xl bg-purple-500/20 border border-purple-500/40 text-purple-200 font-bold text-sm hover:bg-purple-500/30 disabled:opacity-50" data-testid="bulk-ai-resolve-btn">
              {aiBusy ? "🤖…" : "🤖 Risolvi con AI"}
            </button>
            <button onClick={onClose} className="text-slate-500 hover:text-white p-2"><X size={20}/></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="text-slate-500 py-10 text-center">Caricamento suggerimenti…</div>
          ) : unresolved.length === 0 ? (
            <div className="text-emerald-400 py-10 text-center font-bold">✅ Tutti i giocatori sono già collegati al listone!</div>
          ) : unresolved.map(u => (
            <div key={u.player_id} className="rounded-2xl border border-slate-800 bg-slate-950/50 p-3" data-testid={`bulk-item-${u.player_id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full font-black text-[11px] border ${ROLE_STYLES[u.role] || "bg-slate-700"}`}>{u.role || "?"}</span>
                <span className="font-bold text-slate-100">{u.name}</span>
                <span className="text-xs text-slate-500">· {u.real_team || "?"}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-400 border border-slate-700">
                  pagato {u.prezzo_pagato ?? "?"}cr
                </span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${u.reason === "no_fanta_id" ? "bg-rose-500/20 text-rose-300 border border-rose-500/40" : "bg-amber-500/20 text-amber-300 border border-amber-500/40"}`}>
                  {u.reason === "no_fanta_id" ? "🚫 senza fanta_id" : "🔎 non nel file rose"}
                </span>
              </div>
              {u.candidates.length === 0 ? (
                <div className="text-xs text-slate-500 italic pl-2">Nessun candidato fuzzy trovato nel listone globale.</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-1.5">
                  {u.candidates.map(c => {
                    const selected = selections[u.player_id] === c.fanta_id;
                    const statusColor = c.listing_status === "LISTED_ACTIVE" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" :
                                        c.listing_status === "TEMPORARILY_UNLISTED" ? "border-slate-500/40 text-slate-300 bg-slate-600/20" :
                                        c.listing_status === "LISTED_CEDUTO" ? "border-orange-500/40 text-orange-300 bg-orange-500/10" :
                                        "border-cyan-500/40 text-cyan-300 bg-cyan-500/10";
                    return (
                      <button
                        key={c.fanta_id}
                        onClick={() => setSelections(prev => ({ ...prev, [u.player_id]: selected ? null : c.fanta_id }))}
                        data-testid={`bulk-cand-${u.player_id}-${c.fanta_id}`}
                        className={`text-left p-2 rounded-xl border transition-all ${selected ? "border-amber-400 bg-amber-500/15 ring-2 ring-amber-400/40" : "border-slate-800 hover:border-slate-600 bg-slate-900/30"}`}>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-bold text-sm text-slate-100">{c.name}</span>
                          <span className="text-[10px] text-slate-500">({c.real_team})</span>
                        </div>
                        <div className="flex items-center gap-1 mt-1 flex-wrap">
                          <span className={`text-[9px] px-1 py-0.5 rounded-full border ${statusColor}`}>{c.listing_status}</span>
                          <span className="text-[10px] text-slate-500 font-mono">score {(c.score * 100).toFixed(0)}%</span>
                          <span className="text-[10px] text-slate-500 font-mono">Q.A. {c.qa_current ?? "?"}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-xs text-slate-400">
            <strong className="text-amber-300">{Object.values(selections).filter(Boolean).length}</strong> selezioni pronte da applicare
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-2xl border border-slate-700 text-slate-400 hover:bg-slate-800 text-sm font-bold" data-testid="bulk-cancel-btn">Annulla</button>
            <button onClick={applyResolutions} disabled={applying || Object.values(selections).filter(Boolean).length === 0} className="px-4 py-2 rounded-2xl bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 font-black text-sm hover:bg-emerald-500/30 disabled:opacity-50" data-testid="bulk-apply-btn">
              {applying ? "Applico…" : `✅ Applica selezioni`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
